import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { logout } from '../redux/actions/userActions';

export const IDLE_TIMEOUT_MS = 15 * 60 * 1000;
export const LAST_ACTIVITY_STORAGE_KEY = 'watcher_last_activity';

const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'mousemove', 'scroll', 'touchstart'];

const IdleLogout = () => {
  const dispatch = useDispatch();
  const isLoggedIn = useSelector(
    (state) => Boolean(state.userLogin?.userInfo?.session?.loggedin)
  );
  const timeoutRef = useRef(null);
  const lastRecordedRef = useRef(0);

  useEffect(() => {
    if (!isLoggedIn) return undefined;

    const scheduleLogout = () => {
      window.clearTimeout(timeoutRef.current);
      const storedActivity = Number(localStorage.getItem(LAST_ACTIVITY_STORAGE_KEY));
      const lastActivity = Number.isFinite(storedActivity) && storedActivity > 0
        ? storedActivity
        : Date.now();
      const remaining = Math.max(0, IDLE_TIMEOUT_MS - (Date.now() - lastActivity));

      timeoutRef.current = window.setTimeout(() => {
        dispatch(logout());
      }, remaining);
    };

    const recordActivity = () => {
      const now = Date.now();
      // Mouse movement can fire many times per second; one shared update per
      // second is enough to coordinate the timeout across browser tabs.
      if (now - lastRecordedRef.current < 1000) return;
      lastRecordedRef.current = now;
      localStorage.setItem(LAST_ACTIVITY_STORAGE_KEY, String(now));
      scheduleLogout();
    };

    const handleStorage = (event) => {
      if (event.key === LAST_ACTIVITY_STORAGE_KEY) scheduleLogout();
    };

    if (!localStorage.getItem(LAST_ACTIVITY_STORAGE_KEY)) recordActivity();
    else scheduleLogout();

    ACTIVITY_EVENTS.forEach((eventName) => {
      window.addEventListener(eventName, recordActivity, { passive: true });
    });
    window.addEventListener('storage', handleStorage);

    return () => {
      window.clearTimeout(timeoutRef.current);
      ACTIVITY_EVENTS.forEach((eventName) => {
        window.removeEventListener(eventName, recordActivity);
      });
      window.removeEventListener('storage', handleStorage);
    };
  }, [dispatch, isLoggedIn]);

  return null;
};

export default IdleLogout;
